import React from "react";
import {
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import useDisposition from "../../../../hooks/useDisposition";
import NoData from "../../../NoData";

const DispositionBreakdown = ({ dispositionData, trendsData, isLoading = false, className = "" }) => {
  const { donutData, lineChartData, legendData, yAxisDomain } = useDisposition(dispositionData, trendsData);

  const total = donutData.reduce((sum, item) => sum + item.value, 0);

  const hasNoData =
    (!dispositionData || dispositionData.length === 0) && (!trendsData || trendsData.length === 0);
  const hasNoDispositionData = !dispositionData || dispositionData.length === 0;
  const hasNoTrendsData = !trendsData || trendsData.length === 0;

  // Custom tooltip for Pie Chart
  const CustomPieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <div className="bg-gray-900 text-white text-xs rounded-lg py-2 px-3 shadow-lg">
          <div className="font-semibold dark:text-gray-200">{data.name}</div>
          <div className="text-gray-300 dark:text-gray-400">{data.value} calls</div>
          <div className="font-medium dark:text-gray-400">{data.payload.percentage}%</div>
        </div>
      );
    }
    return null;
  };

  const CustomLineTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-gray-900 text-white text-xs rounded-lg py-2 px-3 shadow-lg">
          <div className="font-semibold dark:text-gray-200 mb-1">{label}</div>
          {payload.map((entry, index) => (
            <div key={index} className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
              <span className="text-gray-300 dark:text-gray-400">{entry.dataKey}:</span>
              <span className="font-medium dark:text-gray-200">{entry.value}</span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  if (hasNoData && !isLoading) {
    return (
      <div className={`bg-white dark:bg-[#181D27] rounded-lg p-6 border border-[#E9EAEB] ${className}`}>
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
            Call disposition breakdown
          </h3>
        </div>
        <NoData
          className="py-16"
          heading="No call disposition data available"
          description="Start making calls to see disposition breakdown and trends"
        />
      </div>
    );
  }

  return (
    <div
      className={`bg-white dark:bg-[#0C0E12] rounded-lg p-6 border border-gray-200 dark:border-[#22262F] ${className}`}
    >
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
          Call disposition breakdown
        </h3>
        {isLoading && <p className="text-sm text-gray-500">Loading disposition data...</p>}
      </div>

      <div className="flex flex-col lg:grid lg:grid-cols-[1fr_4fr] gap-6  items-center lg:items-start">
        {/* Donut Chart  */}
        <div className="relative flex justify-center lg:justify-start">
          {hasNoDispositionData ? (
            <div className="w-72 h-72 flex items-center justify-center">
              <NoData
                className="py-8"
                heading="No disposition data"
                description="Call disposition breakdown will appear here"
              />
            </div>
          ) : (
            <div className="w-72 h-72">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={donutData}
                    cx="50%"
                    cy="50%"
                    innerRadius={70}
                    outerRadius={110}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {donutData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomPieTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Legend for Donut Chart - Mobile */}
          {!hasNoDispositionData && (
            <div className="lg:hidden absolute -bottom-6 left-1/2 transform -translate-x-1/2 flex flex-wrap justify-center gap-4 mt-4">
              {donutData.map((item, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                  <div className="text-xs">
                    <span className="font-medium text-gray-900">{item.name}</span>
                    <span className="text-gray-500 ml-1">{item.percentage}%</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Line Chart Container */}
        <div className="relative w-full min-h-[350px] mt-8 lg:mt-0 lg:pl-4 focus:outline-none">
          {hasNoTrendsData ? (
            <div className="w-full h-[320px] flex items-center justify-center">
              <NoData
                className="py-16"
                heading="No trends data"
                description="Disposition trends over time will appear here"
              />
            </div>
          ) : (
            <>
              {/* <div className="hidden lg:flex lg:flex-col gap-2 absolute top-0 right-0 z-10">
                {legendData.map((item) => (
                  <div key={item.name} className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                    <span className="text-xs text-gray-600">{item.name}</span>
                  </div>
                ))}
              </div> */}

              <div className="w-full overflow-x-auto">
                <div className="min-w-[500px] focus:outline-none">
                  <ResponsiveContainer width="100%" height={320}>
                    <LineChart
                      data={lineChartData}
                      margin={{ top: 20, right: 20, left: 0, bottom: 5 }}
                      style={{ outline: "none" }}
                    >
                      <CartesianGrid strokeDasharray="0" stroke="#E5E7EB" vertical={false} />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 12, fill: "#6B7280" }}
                        axisLine={{ stroke: "#E5E7EB" }}
                        tickLine={false}
                      />
                      <YAxis
                        tick={{ fontSize: 12, fill: "#6B7280" }}
                        axisLine={false}
                        tickLine={false}
                        domain={yAxisDomain}
                      />
                      <Tooltip content={<CustomLineTooltip />} />
                      {legendData.map((legend, index) => (
                        <Line
                          key={legend.name}
                          type="monotone"
                          dataKey={legend.name}
                          stroke={legend.color}
                          strokeWidth={2}
                          dot={false}
                          connectNulls={true}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </>
          )}

          {!hasNoTrendsData && (
            <div className="lg:hidden flex flex-wrap justify-center gap-4 mt-4">
              {legendData.map((item) => (
                <div key={item.name} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                  <span className="text-xs text-gray-600">{item.name}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DispositionBreakdown;
